import { useEffect, useRef } from 'react';
import { useSocket } from '../contexts/SocketContext';

/**
 * Hook to listen to socket events
 * @param {string} event - Event name to listen to
 * @param {Function} handler - Handler function for the event
 */
export const useSocketEvent = (event, handler) => {
  const { on, off } = useSocket();
  const handlerRef = useRef(handler);

  // Update handler ref without re-subscribing
  handlerRef.current = handler;

  useEffect(() => {
    if (!event) return;

    // Create stable wrapper that calls current handler
    const stableHandler = (data) => {
      handlerRef.current(data);
    };

    on(event, stableHandler);

    return () => {
      off(event, stableHandler);
    };
  }, [event, on, off]);
};

/**
 * Hook to listen to multiple socket events
 * @param {Object} eventHandlers - Object with event names as keys and handlers as values
 */
export const useSocketEvents = (eventHandlers) => {
  const { on, off } = useSocket();
  const handlersRef = useRef(eventHandlers);

  // Update handlers ref
  handlersRef.current = eventHandlers;

  useEffect(() => {
    const events = Object.keys(eventHandlers);
    const stableHandlers = new Map();

    // Create stable handlers for each event
    events.forEach(event => {
      const stableHandler = (data) => {
        if (handlersRef.current[event]) {
          handlersRef.current[event](data);
        }
      };
      stableHandlers.set(event, stableHandler);
      on(event, stableHandler);
    });

    return () => {
      // Clean up all handlers
      stableHandlers.forEach((handler, event) => {
        off(event, handler);
      });
    };
  }, [Object.keys(eventHandlers).join(','), on, off]);
};

/**
 * Hook to emit socket events
 * @returns {Function} emit function
 */
export const useSocketEmit = () => {
  const { emit } = useSocket();
  return emit;
};

/**
 * Hook to get socket connection status
 * @returns {boolean} connected status
 */
export const useSocketConnection = () => {
  const { connected } = useSocket();
  return connected;
};